import { useRef, useState } from "react";
import { IoMdSend } from "react-icons/io";
import { CgAttachment } from "react-icons/cg";
import { MdClose } from "react-icons/md";
import api from "../../../config/URL";
import { toast } from "react-toastify";
import { formatSingaporeTime } from "../../../utility/formatSingaporeTime";

const MessageInput = ({ conversationId, onSuccess, onOptimisticSend }) => {
  const fileRef = useRef(null);
  const textareaRef = useRef(null);

  const userId = Number(localStorage.getItem("loginUserId"));
  const userType = "USER";

  const [message, setMessage] = useState("");
  const [files, setFiles] = useState([]);

  /* ---------------- AUTO GROW TEXTAREA ---------------- */
  const handleInput = (e) => {
    setMessage(e.target.value);
    e.target.style.height = "auto";
    e.target.style.height = `${e.target.scrollHeight}px`;
  };

  /* ---------------- FILE SELECT ---------------- */
  const handleFileChange = (e) => {
    const selected = Array.from(e.target.files);
    setFiles((prev) => [...prev, ...selected]);
    e.target.value = null;
  };

  /* ---------------- REMOVE FILE ---------------- */
  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  /* ---------------- SEND MESSAGE ---------------- */
  const handleSend = async () => {
    if (!message.trim() && files.length === 0) return;

    const tempMessage = {
      id: Date.now(),
      text: message.trim(),
      media: files.map((file) => ({
        url: URL.createObjectURL(file),
        type: file.type,
      })),
      isRight: true,
      time: formatSingaporeTime(),
    };

    // 🚀 OPTIMISTIC UPDATE
    onOptimisticSend(tempMessage);

    const formData = new FormData();
    formData.append(
      "data",
      JSON.stringify({
        senderId: userId,
        senderType: userType,
        conversationId,
        message: message.trim(),
      }),
    );

    files.forEach((file) => formData.append("attachments", file));

    setMessage("");
    setFiles([]);
    if (textareaRef.current) textareaRef.current.style.height = "40px";

    try {
      await api.post("/sendChatMessage", formData);
      onSuccess(); // sync with backend
    } catch {
      toast.error("Message send failed");
    }
  };

  /* ---------------- ENTER TO SEND ---------------- */
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div
      className="border-top bg-white p-2"
      style={{
        position: "sticky",
        bottom: "20px",
        zIndex: 10,
      }}
    >
      {/* FILE PREVIEW */}
      {files.length > 0 && (
        <div className="d-flex gap-2 mb-2 flex-wrap">
          {files.map((file, index) => {
            const isImage = file.type.startsWith("image/");
            const previewUrl = isImage ? URL.createObjectURL(file) : null;

            return (
              <div
                key={index}
                style={{
                  position: "relative",
                  width: 80,
                  height: 80,
                  borderRadius: 8,
                  overflow: "hidden",
                  border: "1px solid #ddd",
                }}
              >
                {isImage ? (
                  <img
                    src={previewUrl}
                    alt=""
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <div
                    className="d-flex align-items-center justify-content-center h-100 text-center"
                    style={{ fontSize: 12, padding: 4 }}
                  >
                    {file.name}
                  </div>
                )}

                <button
                  onClick={() => removeFile(index)}
                  style={{
                    position: "absolute",
                    top: 2,
                    right: 2,
                    background: "#000",
                    border: "none",
                    borderRadius: "50%",
                    width: 20,
                    height: 20,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    cursor: "pointer",
                  }}
                >
                  <MdClose size={14} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* INPUT ROW */}
      <div className="d-flex align-items-end gap-2">
        <button
          className="btn btn-light"
          onClick={() => fileRef.current.click()}
          title="Attach file"
        >
          <CgAttachment size={20} />
        </button>

        <textarea
          ref={textareaRef}
          value={message}
          onChange={handleInput}
          onKeyDown={handleKeyDown}
          placeholder="Type a message"
          rows={1}
          className="form-control"
          style={{
            resize: "none",
            maxHeight: 120,
            overflowY: "auto",
          }}
        />

        <button
          className="btn btn-success"
          onClick={handleSend}
          disabled={!message.trim() && files.length === 0}
        >
          <IoMdSend size={18} />
        </button>

        <input
          type="file"
          ref={fileRef}
          hidden
          multiple
          onChange={handleFileChange}
        />
      </div>
    </div>
  );
};

export default MessageInput;
